import { combineReducers } from 'redux';
import { ActionType, getType } from 'typesafe-actions';

import FeaturesWithCategories from '../models/FeaturesWithCategories';
import * as actions from './actions';
import initialState from './initial-state';

export type Action = ActionType<typeof actions>;

export interface State {
  basemapStyle: 'satellite' | 'road';
  isSidePanelOpen: boolean;
  identify: { entities: any; selectedId: number | null };
  sectionals: { isVisible: boolean };
  tobinMaps: { isVisible: boolean };
  internalMowing: {
    isVisible: boolean;
    isSettingsOpen: boolean;
    startDate: null | Date;
    endDate: null | Date;
    features: any;
  };
  publicMowing: { features: any };
  abstracts: { isVisible: boolean };
  ditches: FeaturesWithCategories;
  beaumontZoning: { isVisible: boolean };
  counties: { isVisible: boolean };
  schoolDistricts: { isVisible: boolean };
  subdivisions: { isVisible: boolean };
  commissionerPrecincts: { isVisible: boolean };
  drainageDistricts: { isVisible: boolean };
  parcels: { isVisible: boolean };
  subwatersheds: { isVisible: boolean };
  watersheds: { isVisible: boolean };
  ownership: FeaturesWithCategories;
  searchLocation: {
    latitude: number;
    longitude: number;
  } | null;
}

export default combineReducers<State, Action>({
  basemapStyle: (state = initialState.basemapStyle, action) => {
    switch (action.type) {
      case getType(actions.toggleBasemap):
        return state === 'satellite' ? 'road' : 'satellite';
      default:
        return state;
    }
  },
  isSidePanelOpen: (state = initialState.isSidePanelOpen, action) => {
    switch (action.type) {
      case getType(actions.toggleSidePanel):
        return !state;
      default:
        return state;
    }
  },
  identify: (state = initialState.identify, action) => {
    switch (action.type) {
      case getType(actions.setIdentifiedEntities):
        const selectedEntiies = action.payload;
        return {
          entities: selectedEntiies,
          selectedId: selectedEntiies.length === 1 ? selectedEntiies[0].id : null,
        };
      case getType(actions.setSelectedId):
        return { ...state, selectedId: action.payload };
      case getType(actions.clearIdentify):
        return initialState.identify;
      case getType(actions.clearSelectedId):
        return { ...state, selectedId: null };
      default:
        return state;
    }
  },
  sectionals: (state = initialState.sectionals, action) => {
    switch (action.type) {
      case getType(actions.toggleSectionalsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  tobinMaps: (state = initialState.tobinMaps, action) => {
    switch (action.type) {
      case getType(actions.toggleTobinMapsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  internalMowing: (state = initialState.internalMowing, action) => {
    switch (action.type) {
      case getType(actions.toggleInternalMowingSettings):
        return { ...state, isSettingsOpen: !state.isSettingsOpen };
      case getType(actions.toggleInternalMowingVisibility):
        const updatedVisibility = !state.isVisible;
        const updatedIsSettingsOpen = !updatedVisibility ? false : state.isSettingsOpen;
        return { ...state, isVisible: updatedVisibility, isSettingsOpen: updatedIsSettingsOpen };
      case getType(actions.setInternalMowingStartDate):
        return { ...state, startDate: action.payload };
      case getType(actions.setInternalMowingEndDate):
        return { ...state, endDate: action.payload };
      case getType(actions.setInternalMowingFeatures):
        return { ...state, features: action.payload };
      default:
        return state;
    }
  },
  publicMowing: (state = initialState.publicMowing, action) => {
    switch (action.type) {
      case getType(actions.setPublicMowingFeatures):
        return { ...state, features: action.payload };
      default:
        return state;
    }
  },
  abstracts: (state = initialState.abstracts, action) => {
    switch (action.type) {
      case getType(actions.toggleAbstractsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  ditches: (state = initialState.ditches, action) => {
    switch (action.type) {
      case getType(actions.toggleDitchesSettings):
        return { ...state, isSettingsOpen: !state.isSettingsOpen };
      case getType(actions.toggleDitchesVisibility):
        const updatedVisibility = !state.isVisible;
        const updatedIsSettingsOpen = !updatedVisibility ? false : state.isSettingsOpen;
        return { ...state, isVisible: updatedVisibility, isSettingsOpen: updatedIsSettingsOpen };
      case getType(actions.toggleDitchesLayer):
        const updatedLayers = state.categoryTypes.map((layer) => {
          if (layer.name === action.payload) {
            return { ...layer, isVisible: !layer.isVisible };
          }
          return layer;
        });
        return { ...state, categoryTypes: updatedLayers };
      default:
        return state;
    }
  },
  beaumontZoning: (state = initialState.beaumontZoning, action) => {
    switch (action.type) {
      case getType(actions.toggleBeaumontZoningVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  counties: (state = initialState.counties, action) => {
    switch (action.type) {
      case getType(actions.toggleCountiesVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  schoolDistricts: (state = initialState.schoolDistricts, action) => {
    switch (action.type) {
      case getType(actions.toggleSchoolDistrictsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  subdivisions: (state = initialState.subdivisions, action) => {
    switch (action.type) {
      case getType(actions.toggleSubdivisionsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  commissionerPrecincts: (state = initialState.commissionerPrecincts, action) => {
    switch (action.type) {
      case getType(actions.toggleCommissionerPrecinctsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  drainageDistricts: (state = initialState.drainageDistricts, action) => {
    switch (action.type) {
      case getType(actions.toggleDrainageDistrictsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  parcels: (state = initialState.parcels, action) => {
    switch (action.type) {
      case getType(actions.toggleParcelsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  subwatersheds: (state = initialState.subwatersheds, action) => {
    switch (action.type) {
      case getType(actions.toggleSubwatershedsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  watersheds: (state = initialState.watersheds, action) => {
    switch (action.type) {
      case getType(actions.toggleWatershedsVisibility):
        return { ...state, isVisible: !state.isVisible };
      default:
        return state;
    }
  },
  ownership: (state = initialState.ownership, action) => {
    switch (action.type) {
      case getType(actions.toggleOwnershipSettings):
        return { ...state, isSettingsOpen: !state.isSettingsOpen };
      case getType(actions.toggleOwnershipVisibility):
        const updatedVisibility = !state.isVisible;
        const updatedIsSettingsOpen = !updatedVisibility ? false : state.isSettingsOpen;
        return { ...state, isVisible: updatedVisibility, isSettingsOpen: updatedIsSettingsOpen };
      case getType(actions.toggleOwnershipLayer):
        const updatedLayers = state.categoryTypes.map((layer) => {
          if (layer.name === action.payload) {
            return { ...layer, isVisible: !layer.isVisible };
          }
          return layer;
        });
        return { ...state, categoryTypes: updatedLayers };
      default:
        return state;
    }
  },
  searchLocation: (state = initialState.searchLocation, action) => {
    switch (action.type) {
      case getType(actions.setSearchLocation):
        return action.payload;
      default:
        return state;
    }
  },
});
